import { useEffect, useState } from 'react';
import { useApi } from '../../../app/providers';
import { href, navigate } from '../../../app/router';
import { useInvalidateContent } from '../../dashboard/queries';
import type { LocalMediaStore } from '../../media/store';
import type { createBoardMediaSession } from '../../media/session';
import type { processInWorker } from '../../media/import';
import { FigmaImportDialog } from './FigmaImportDialog';

type Media = { local: LocalMediaStore; create: typeof createBoardMediaSession; process: typeof processInWorker };

/** New board › Import from Figma (backlog §12): the import's board gets its images through the P4 pipeline. */
export function FigmaNewBoardDialog({ orgId, projectId, folderId, onClose }: { orgId: string; projectId: string; folderId: string | null; onClose: () => void }) {
  const api = useApi();
  const invalidate = useInvalidateContent();
  const [media, setMedia] = useState<Media | null>(null);
  useEffect(() => {
    let live = true;
    let opened: LocalMediaStore | null = null;
    void Promise.all([import('../../media/store'), import('../../media/session'), import('../../media/import')]).then(async ([{ LocalMediaStore }, { createBoardMediaSession }, { processInWorker }]) => {
      const local = await LocalMediaStore.open();
      if (!live) return local.close();
      opened = local;
      setMedia({ local, create: createBoardMediaSession, process: processInWorker });
    });
    return () => {
      live = false;
      opened?.close();
    };
  }, []);
  if (!media) return null;
  return (
    <FigmaImportDialog
      mode="new"
      api={api}
      orgId={orgId}
      projectId={projectId}
      folderId={folderId}
      session={(boardId, store) => media.create({ boardId, api, store, local: media.local, process: media.process })}
      onClose={() => {
        void invalidate();
        onClose();
      }}
      onCreated={(boardId) => {
        void invalidate();
        onClose();
        navigate(href('board', { boardId }));
      }}
    />
  );
}
